import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Bike, MapPin, Route as RouteIcon, Mountain } from "lucide-react";
import MapView from "@/components/MapView";
import { useI18n } from "@/lib/i18n";

export default function Rides() {
  const { t } = useI18n();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/routes");
      setRides(data);
      if (data.length > 0 && !selected) setSelected(data[0]);
    } catch (e) {
      toast.error(t("ridesLoadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const del = async (id) => {
    try {
      await api.delete(`/routes/${id}`);
      setRides((r) => r.filter((x) => x.route_id !== id));
      if (selected?.route_id === id) setSelected(null);
      toast.success(t("ridesDeleted"));
    } catch {
      toast.error(t("ridesDeleteError"));
    }
  };

  return (
    <div className="min-h-screen text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="font-mono text-[10px] text-brema-yellow uppercase tracking-[0.3em]">
            {t("logbook")}
          </div>
          <h1 className="font-display font-black uppercase text-4xl tracking-tight">
            {t("ridesTitle")}
          </h1>
        </div>

        {loading ? (
          <div className="text-zinc-200 font-mono uppercase tracking-widest text-xs">
            {t("loading")}
          </div>
        ) : rides.length === 0 ? (
          <div className="relative border border-dashed border-white/20 p-16 text-center bg-brema-coal">
            <RouteIcon className="mx-auto text-brema-yellow" size={40} />
            <h2 className="font-display font-bold uppercase text-2xl mt-4">
              {t("noSavedLoop")}
            </h2>
            <p className="text-zinc-200 mt-2 text-sm">
              {t("ridesEmptyDesc")}
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[380px_1fr] gap-6">
            <div className="space-y-3">
              {rides.map((r) => (
                <div
                  key={r.route_id}
                  onClick={() => setSelected(r)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setSelected(r);
                  }}
                  role="button"
                  tabIndex={0}
                  data-testid={`ride-card-${r.route_id}`}
                  className={`w-full text-left relative border p-4 bg-brema-coal transition-all group cursor-pointer ${
                    selected?.route_id === r.route_id
                      ? "border-brema-yellow"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <span className="corner-tick tl" />
                  <span className="corner-tick br" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-brema-yellow">
                      <Bike size={14} />
                      <span className="font-mono text-[10px] uppercase tracking-widest">
                        {r.vehicle}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        del(r.route_id);
                      }}
                      data-testid={`delete-ride-${r.route_id}`}
                      className="text-zinc-200 hover:text-brema-red p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="font-display font-bold uppercase text-xl mt-2 tracking-tight text-white leading-tight">
                    {r.name}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-100">
                    <div>
                      <div className="text-zinc-100">Dist</div>
                      <div className="text-brema-yellow font-bold">
                        {r.distance_km}km
                      </div>
                    </div>
                    <div>
                      <div className="text-zinc-100">Off</div>
                      <div className="text-brema-red font-bold">
                        {r.offroad_percent}%
                      </div>
                    </div>
                    <div>
                      <div className="text-zinc-100">Lvl</div>
                      <div className="text-white font-bold">{r.difficulty}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative bg-brema-coal border border-white/10 h-[560px] overflow-hidden">
              <span className="corner-tick tl" />
              <span className="corner-tick tr" />
              <span className="corner-tick bl" />
              <span className="corner-tick br" />
              {selected && (
                <>
                  <MapView
                    start={{ lat: selected.start_lat, lng: selected.start_lng }}
                    polyline={selected.polyline}
                  />
                  <div className="absolute bottom-4 left-4 right-4 z-[400] bg-brema-obsidian/85 backdrop-blur border border-brema-yellow/30 p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MiniStat icon={<MapPin size={14} />} label={t("startShort")} value={selected.start_label} />
                    <MiniStat icon={<RouteIcon size={14} />} label={t("distance")} value={`${selected.distance_km} km`} />
                    <MiniStat icon={<Mountain size={14} />} label={t("elevation")} value={`${selected.elevation_gain_m} m`} />
                    <MiniStat label={t("difficultyShort")} value={selected.difficulty} accent />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function MiniStat({ icon, label, value, accent }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-zinc-200 font-mono text-[9px] uppercase tracking-widest">
        {icon} {label}
      </div>
      <div
        className={`mt-1 font-display font-bold text-sm uppercase tracking-wide ${
          accent ? "text-brema-red" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
